import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { Flex, Box } from '@rebass/emotion';

// cheeky temp screen to add products to cart

import { isNotNullish } from '@app/helpers/predicate';
import Loader from '@app/components/Loader';
import Link from '@app/components/Link';
import PageTitle from '@app/components/PageTitle';
import ErrorBoundary from '@app/components/ErrorBoundary';
import { AlertItem } from '@app/shop/hooks/useAlerts';
import { AlertMessage, AlertTitle } from '@app/shop/components/Alert';

import { CartProvider } from '../../context/CartContext';
import { ShopStoreProvider } from '../../context/ShopStoreContext';
import { Button } from '../../components/Button';
import Alerts from '../../components/Alerts';
import useShopStore from '../../hooks/useShopStore';
import useCart from '../../hooks/useCart';
import StoreSelection from './StoreSelection';
import CartRefreshButton from './CartRefreshButton';
import ItemCard from './ItemCard';
import { TEMP_PRODUCTS_QUERY } from './TempAddProducts.queries';

import {
  TempProductsQuery,
  TempProductsQueryVariables,
} from './__generated__/TempProductsQuery';

const TempAddProducts: FC = () => (
  <>
    <ErrorBoundary>
      <StoreSelection />
      <ShopStoreProvider>
        <CartProvider>
          <AddProducts />
        </CartProvider>
      </ShopStoreProvider>
    </ErrorBoundary>
  </>
);

export default TempAddProducts;

const AddProducts: FC = () => {
  const { storeCode } = useShopStore();
  const {
    cartId,
    cartUserType,
    loading: cartLoading,
    items: cartItems,
    errors,
    userErrors,
  } = useCart();

  const { fetchMore, ...productsResult } = useQuery<
    TempProductsQuery,
    TempProductsQueryVariables
  >(TEMP_PRODUCTS_QUERY, {
    skip: !cartId,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
    variables: { currentPage: 1 },
  });

  const { items: productItems, pageInfo } =
    productsResult.data?.magento2?.products ?? {};
  const currentPage = pageInfo?.currentPage ?? 1;
  const hasNextPage = currentPage < (pageInfo?.totalPages ?? 1);
  const validItems = useMemo(() => productItems?.filter(isNotNullish) ?? [], [
    productItems,
  ]);

  const loadMore = useCallback(
    () =>
      fetchMore({
        variables: { currentPage: (currentPage ?? 1) + 1 },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          if (hasNextPage && fetchMoreResult?.magento2?.products?.items) {
            fetchMoreResult.magento2.products.items = [
              ...(previousResult?.magento2?.products?.items ?? []),
              ...(fetchMoreResult?.magento2?.products?.items ?? []),
            ];

            return fetchMoreResult;
          }

          return previousResult;
        },
      }),
    [fetchMore, hasNextPage, currentPage]
  );

  const [error, setError] = useState(false);

  useEffect(() => {
    if (error) throw new Error('Boom');
  }, [error]);

  const handleLoadMoreClick = useCallback(() => {
    if (productsResult.loading) return;
    loadMore();
  }, [loadMore, productsResult.loading]);

  const cartItemsCount = cartItems?.reduce(
    (acc, item) => acc + (item?.quantity ?? 0),
    0
  );

  return (
    <>
      <PageTitle>
        {{ id: 'TEMP_NO_LOCIZE', defaultMessage: 'ADD PRODUCTS TO CART' }}
      </PageTitle>
      <Box mx={2} my={20}>
        <Alerts
          data-testid="cart-alerts"
          alerts={
            [
              {
                type: storeCode ? 'success' : 'info',
                content: (
                  <AlertTitle>
                    {storeCode ? `store: ${storeCode}` : 'resolving store...'}
                  </AlertTitle>
                ),
              },
              {
                type: cartId ? 'success' : 'info',
                content: (
                  <AlertTitle>
                    {cartId
                      ? `cart: ${cartUserType}, ${cartId}`
                      : 'resolving cart...'}
                  </AlertTitle>
                ),
              },
              ...(errors?.length
                ? errors.map((error) => ({
                    type: 'error',
                    content: (
                      <>
                        <AlertTitle>Error</AlertTitle>
                        <AlertMessage>{error.message}</AlertMessage>
                      </>
                    ),
                  }))
                : []),
              ...(userErrors?.length
                ? userErrors.map((error) => ({
                    type: 'error',
                    title: 'User Error',
                    content: (
                      <>
                        <AlertTitle>User Error</AlertTitle>
                        <AlertMessage>
                          {error.message} (code: {error.code})
                        </AlertMessage>
                      </>
                    ),
                  }))
                : []),
            ] as AlertItem[]
          }
        />
      </Box>
      <Box m={2}>
        <Flex alignItems="center" mb={2} ml={2}>
          <CartRefreshButton />
          <Flex alignItems="center" ml={2}>
            <Link to="/:language/shop/cart/">
              <Button type="primary" as="div" data-testid="go-to-cart-button">
                Go to cart
              </Button>
            </Link>

            <Box ml={2}>
              {cartItemsCount} item{cartItemsCount === 1 ? '' : 's'}
            </Box>
            <Box ml={4}>
              {productsResult.loading || cartLoading ? <Loader /> : null}
            </Box>
          </Flex>
        </Flex>
        <Box m={2}>
          <Button
            type="secondary"
            onClick={() => {
              setError(true);
            }}
          >
            Error out
          </Button>
        </Box>
        <Flex flexWrap="wrap">
          {validItems.map((item) => (
            <ItemCard item={item} key={item.uid} />
          ))}
        </Flex>
        {hasNextPage && (
          <Flex m={2} justifyContent="center">
            <Button
              wide
              type="primary"
              onClick={handleLoadMoreClick}
              isLoading={productsResult.loading}
              disabled={productsResult.loading}
              data-testid="load-more-products-button"
            >
              Load more
            </Button>
          </Flex>
        )}
      </Box>
    </>
  );
};
